import type { Ref, SelectHTMLAttributes } from 'react';
import { cx } from '@/lib/cx';

export interface SelectProps extends Omit<SelectHTMLAttributes<HTMLSelectElement>, 'size'> {
  ref?: Ref<HTMLSelectElement>;
  invalid?: boolean;
}

/*
 * A styled native <select>. Deliberately native: it inherits the platform's
 * keyboard handling, type-ahead and mobile picker for free, which a custom
 * listbox has to reimplement and usually gets subtly wrong.
 */
export function Select({ invalid, className, children, ...rest }: SelectProps) {
  return (
    <div className="relative">
      <select
        aria-invalid={invalid || undefined}
        className={cx(
          'peer h-9 w-full appearance-none rounded-md border border-border bg-surface pr-8 pl-2.5 text-sm text-fg max-sm:h-11',
          'transition-colors duration-150 ease-out motion-reduce:transition-none',
          'hover:border-hairline-strong',
          'disabled:cursor-not-allowed disabled:opacity-50 disabled:hover:border-border',
          'aria-invalid:border-danger',
          className,
        )}
        {...rest}
      >
        {children}
      </select>
      <span
        aria-hidden="true"
        className="pointer-events-none absolute top-1/2 right-2.5 -translate-y-1/2 text-fg-subtle peer-disabled:opacity-50"
      >
        <svg
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth={1.75}
          strokeLinecap="round"
          strokeLinejoin="round"
          className="h-4 w-4"
        >
          <path d="m6 9 6 6 6-6" />
        </svg>
      </span>
    </div>
  );
}
